import { useCallback, useEffect, useRef, useState } from "react";
import useSWR, { useSWRConfig } from "swr";
import type { AxiosInstance } from "axios";
import {
  DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY,
  DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER,
  MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES,
  MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES,
  deleteMapPlotRequestDetailFile,
  getMapPlotRequestDetailFileDownload,
  listMapPlotRequestDetailFiles,
  type MapPlotRequestDetailFileSortBy,
  type MapPlotRequestDetailFileSortOrder,
  updateMapPlotRequestDetailFileDisplayName,
  uploadMapPlotRequestDetailFile,
} from "@/features/mapPlotRequest/api/mapPlotRequestDetailFiles";
import {
  type MapPlotRequestDetailFile,
  type MapPlotRequestDetailFileDownloadResponse,
  type MapPlotRequestDetailFileListResponse,
} from "@/features/map/types";
import { useApiClient } from "@/hooks/useApiClient";
import {
  MAP_PLOT_REQUEST_DETAIL_UPLOAD_BATCH_SIZE,
  extractMapPlotRequestDetailFileErrorMessage,
  splitIntoBatches,
} from "@/features/mapPlotRequest/utils/mapPlotRequestDetailFiles";

export interface MapPlotRequestDetailFileUploadProgress {
  total: number;
  processed: number;
  successCount: number;
  failedCount: number;
  currentFileName: string | null;
}

/**
 * 資料アップロード実行結果のサマリー。
 */
export interface MapPlotRequestDetailFileUploadSummary {
  successCount: number;
  failedCount: number;
}

/**
 * アップロード失敗ファイルと、ユーザー表示用メッセージ。
 */
export interface FailedMapPlotRequestDetailFileUpload {
  file: File;
  message: string;
}

const DEFAULT_PAGE_SIZE = 20;
const MAX_VISITED_PAGE_CACHES = 10;
const DEFAULT_QUERY: MapPlotRequestDetailFilesQuery = {
  search: "",
  sortBy: DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY,
  sortOrder: DEFAULT_MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER,
};

export interface MapPlotRequestDetailFilesQuery {
  search: string;
  sortBy: MapPlotRequestDetailFileSortBy;
  sortOrder: MapPlotRequestDetailFileSortOrder;
}

export interface MapPlotRequestDetailFilesResult {
  files: MapPlotRequestDetailFile[];
  totalCount: number;
  page: number;
  pageSize: number;
  changePage: (newPage: number) => void;
  changePageSize: (newPageSize: number) => void;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<MapPlotRequestDetailFileListResponse>;
  uploadFiles: (
    filesToUpload: File[]
  ) => Promise<MapPlotRequestDetailFileUploadSummary>;
  retryFailedUploads: () => Promise<MapPlotRequestDetailFileUploadSummary>;
  deleteFile: (fileId: number) => Promise<void>;
  updateDisplayName: (fileId: number, displayName: string) => Promise<void>;
  getDownloadInfo: (
    fileId: number,
    preview?: boolean
  ) => Promise<MapPlotRequestDetailFileDownloadResponse>;
  isUploading: boolean;
  uploadProgress: MapPlotRequestDetailFileUploadProgress;
  failedUploads: FailedMapPlotRequestDetailFileUpload[];
  lastUploadSummary: MapPlotRequestDetailFileUploadSummary | null;
  resetUploadState: () => void;
}

const isMapPlotRequestDetailFileSortBy = (
  value: string | null
): value is MapPlotRequestDetailFileSortBy => {
  return (
    value !== null &&
    MAP_PLOT_REQUEST_DETAIL_FILE_SORT_BY_VALUES.includes(
      value as MapPlotRequestDetailFileSortBy
    )
  );
};

const isMapPlotRequestDetailFileSortOrder = (
  value: string | null
): value is MapPlotRequestDetailFileSortOrder => {
  return (
    value !== null &&
    MAP_PLOT_REQUEST_DETAIL_FILE_SORT_ORDER_VALUES.includes(
      value as MapPlotRequestDetailFileSortOrder
    )
  );
};

const buildMapPlotRequestDetailFilesKey = (
  detailId: number,
  page: number,
  pageSize: number,
  query: MapPlotRequestDetailFilesQuery
): string => {
  const searchParams = new URLSearchParams({
    page: String(page + 1),
    size: String(pageSize),
    search: query.search,
    sortBy: query.sortBy,
    sortOrder: query.sortOrder,
  });

  return `/map/plot-request-details/${detailId}/files?${searchParams.toString()}`;
};

const createInitialUploadProgress =
  (): MapPlotRequestDetailFileUploadProgress => {
    return {
      total: 0,
      processed: 0,
      successCount: 0,
      failedCount: 0,
      currentFileName: null,
    };
  };

export const useMapPlotRequestDetailFilesWithApiClient = (
  apiClient: AxiosInstance,
  detailId: number | null,
  shouldFetch: boolean,
  query: MapPlotRequestDetailFilesQuery = DEFAULT_QUERY
): MapPlotRequestDetailFilesResult => {
  const { mutate: globalMutate } = useSWRConfig();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(
    createInitialUploadProgress()
  );
  const [failedUploads, setFailedUploads] = useState<
    FailedMapPlotRequestDetailFileUpload[]
  >([]);
  const [lastUploadSummary, setLastUploadSummary] =
    useState<MapPlotRequestDetailFileUploadSummary | null>(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const visitedPageCacheKeysRef = useRef<string[]>([]);
  const { search, sortBy, sortOrder } = query;

  const fetcher =
    useCallback(async (): Promise<MapPlotRequestDetailFileListResponse> => {
      if (detailId === null) {
        return { count: 0, list: [] };
      }

      return await listMapPlotRequestDetailFiles(
        apiClient,
        detailId,
        page + 1,
        pageSize,
        search,
        sortBy,
        sortOrder
      );
    }, [apiClient, detailId, page, pageSize, search, sortBy, sortOrder]);

  const swrResponse = useSWR<MapPlotRequestDetailFileListResponse, Error>(
    detailId !== null && shouldFetch
      ? buildMapPlotRequestDetailFilesKey(detailId, page, pageSize, query)
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateIfStale: true,
      revalidateOnReconnect: false,
      shouldRetryOnError: false,
    }
  );

  const resetUploadState = useCallback((): void => {
    setUploadProgress(createInitialUploadProgress());
    setFailedUploads([]);
    setLastUploadSummary(null);
  }, []);

  useEffect(() => {
    setPage(0);
    setPageSize(DEFAULT_PAGE_SIZE);
    visitedPageCacheKeysRef.current = [];
    resetUploadState();
  }, [detailId, resetUploadState]);

  useEffect(() => {
    visitedPageCacheKeysRef.current = [];
    setPage(0);
  }, [search, sortBy, sortOrder]);

  useEffect(() => {
    if (detailId === null || !shouldFetch) {
      return;
    }

    const currentKey = buildMapPlotRequestDetailFilesKey(
      detailId,
      page,
      pageSize,
      { search, sortBy, sortOrder }
    );

    visitedPageCacheKeysRef.current = [
      currentKey,
      ...visitedPageCacheKeysRef.current.filter((key) => key !== currentKey),
    ].slice(0, MAX_VISITED_PAGE_CACHES);
  }, [detailId, page, pageSize, search, sortBy, sortOrder, shouldFetch]);

  const refetch =
    useCallback(async (): Promise<MapPlotRequestDetailFileListResponse> => {
      if (detailId === null) {
        return { count: 0, list: [] };
      }

      const result = await listMapPlotRequestDetailFiles(
        apiClient,
        detailId,
        page + 1,
        pageSize,
        search,
        sortBy,
        sortOrder
      );
      await swrResponse.mutate(result, false);
      return result;
    }, [
      apiClient,
      detailId,
      page,
      pageSize,
      search,
      sortBy,
      sortOrder,
      swrResponse,
    ]);

  const refreshPageCache = useCallback(
    async (targetPage: number): Promise<void> => {
      if (detailId === null) {
        return;
      }

      const result = await listMapPlotRequestDetailFiles(
        apiClient,
        detailId,
        targetPage + 1,
        pageSize,
        search,
        sortBy,
        sortOrder
      );

      await globalMutate(
        buildMapPlotRequestDetailFilesKey(detailId, targetPage, pageSize, {
          search,
          sortBy,
          sortOrder,
        }),
        result,
        false
      );
    },
    [apiClient, detailId, pageSize, search, sortBy, sortOrder, globalMutate]
  );

  const refreshVisitedPageCachesSafely =
    useCallback(async (): Promise<void> => {
      if (detailId === null) {
        return;
      }

      await Promise.all(
        visitedPageCacheKeysRef.current.map(async (key) => {
          const params = new URLSearchParams(key.split("?")[1] ?? "");
          const targetPage = Number(params.get("page"));
          const targetPageSize = Number(params.get("size"));
          const targetSearch = params.get("search") ?? "";
          const targetSortBy = params.get("sortBy");
          const targetSortOrder = params.get("sortOrder");

          if (
            !Number.isInteger(targetPage) ||
            !Number.isInteger(targetPageSize) ||
            targetPage <= 0 ||
            targetPageSize <= 0 ||
            !isMapPlotRequestDetailFileSortBy(targetSortBy) ||
            !isMapPlotRequestDetailFileSortOrder(targetSortOrder)
          ) {
            return;
          }

          try {
            const result = await listMapPlotRequestDetailFiles(
              apiClient,
              detailId,
              targetPage,
              targetPageSize,
              targetSearch,
              targetSortBy,
              targetSortOrder
            );

            await globalMutate(key, result, false);
          } catch {
            // 一覧再取得失敗で本体操作（アップロード/削除/表示名更新）を失敗扱いにしない
          }
        })
      );
    }, [apiClient, detailId, globalMutate]);

  const refreshPageCacheSafely = useCallback(
    async (targetPage: number): Promise<void> => {
      try {
        await refreshPageCache(targetPage);
      } catch {
        // 一覧再取得失敗で本体操作（アップロード/削除）を失敗扱いにしない
      }
    },
    [refreshPageCache]
  );

  const uploadFiles = useCallback(
    async (
      filesToUpload: File[]
    ): Promise<MapPlotRequestDetailFileUploadSummary> => {
      if (detailId === null || filesToUpload.length === 0) {
        return { successCount: 0, failedCount: 0 };
      }

      setIsUploading(true);
      setFailedUploads([]);
      setLastUploadSummary(null);
      setUploadProgress({
        total: filesToUpload.length,
        processed: 0,
        successCount: 0,
        failedCount: 0,
        currentFileName: null,
      });

      let processed = 0;
      let successCount = 0;
      let failedCount = 0;
      const nextFailedUploads: FailedMapPlotRequestDetailFileUpload[] = [];

      try {
        const batches = splitIntoBatches(
          filesToUpload,
          MAP_PLOT_REQUEST_DETAIL_UPLOAD_BATCH_SIZE
        );

        for (const batch of batches) {
          const batchFailedUploads: Array<FailedMapPlotRequestDetailFileUpload | null> =
            batch.map(() => null);

          await Promise.all(
            batch.map(async (file, index) => {
              setUploadProgress((prev) => ({
                ...prev,
                currentFileName: file.name,
              }));

              try {
                await uploadMapPlotRequestDetailFile(apiClient, detailId, file);
                successCount += 1;
              } catch (error) {
                failedCount += 1;
                batchFailedUploads[index] = {
                  file,
                  message: extractMapPlotRequestDetailFileErrorMessage(
                    error,
                    `「${file.name}」のアップロードに失敗しました`
                  ),
                };
              }

              processed += 1;
              setUploadProgress({
                total: filesToUpload.length,
                processed,
                successCount,
                failedCount,
                currentFileName: file.name,
              });
            })
          );

          nextFailedUploads.push(
            ...batchFailedUploads.filter(
              (item): item is FailedMapPlotRequestDetailFileUpload =>
                item !== null
            )
          );
        }
      } finally {
        setIsUploading(false);
      }

      const summary = {
        successCount,
        failedCount,
      };

      setFailedUploads(nextFailedUploads);
      setLastUploadSummary(summary);
      setUploadProgress((prev) => ({
        ...prev,
        currentFileName: null,
      }));

      if (successCount > 0) {
        if (page !== 0) {
          setPage(0);
          await refreshPageCacheSafely(0);
        }

        await refreshVisitedPageCachesSafely();
      }

      return summary;
    },
    [
      apiClient,
      detailId,
      page,
      refreshPageCacheSafely,
      refreshVisitedPageCachesSafely,
    ]
  );

  const retryFailedUploads = useCallback(async () => {
    if (failedUploads.length === 0) {
      return { successCount: 0, failedCount: 0 };
    }

    const retryTargets = failedUploads.map((item) => item.file);
    return await uploadFiles(retryTargets);
  }, [failedUploads, uploadFiles]);

  const getDownloadInfo = useCallback(
    async (
      fileId: number,
      preview = false
    ): Promise<MapPlotRequestDetailFileDownloadResponse> => {
      if (detailId === null) {
        throw new Error("ダウンロードURLの取得に失敗しました");
      }

      return await getMapPlotRequestDetailFileDownload(
        apiClient,
        detailId,
        fileId,
        preview
      );
    },
    [apiClient, detailId]
  );

  const updateDisplayName = useCallback(
    async (fileId: number, displayName: string): Promise<void> => {
      if (detailId === null) {
        return;
      }

      await updateMapPlotRequestDetailFileDisplayName(
        apiClient,
        detailId,
        fileId,
        displayName
      );

      let nextPageToRefresh: number | null = null;
      try {
        const currentResult = await listMapPlotRequestDetailFiles(
          apiClient,
          detailId,
          page + 1,
          pageSize,
          search,
          sortBy,
          sortOrder
        );
        const lastPageIndex = Math.max(
          0,
          Math.ceil(currentResult.count / pageSize) - 1
        );

        if (page > lastPageIndex) {
          nextPageToRefresh = lastPageIndex;
          setPage(lastPageIndex);
        } else {
          await swrResponse.mutate(currentResult, false);
        }
      } catch {
        // 一覧再取得失敗で表示名更新を失敗扱いにしない
      }

      if (nextPageToRefresh !== null) {
        await refreshPageCacheSafely(nextPageToRefresh);
      }

      await refreshVisitedPageCachesSafely();
    },
    [
      apiClient,
      detailId,
      page,
      pageSize,
      refreshPageCacheSafely,
      refreshVisitedPageCachesSafely,
      search,
      sortBy,
      sortOrder,
      swrResponse,
    ]
  );

  const deleteFile = useCallback(
    async (fileId: number): Promise<void> => {
      if (detailId === null) {
        return;
      }

      await deleteMapPlotRequestDetailFile(apiClient, detailId, fileId);

      const currentCount = swrResponse.data?.count ?? 0;
      const nextCount = Math.max(0, currentCount - 1);
      const lastPageIndex = Math.max(0, Math.ceil(nextCount / pageSize) - 1);

      if (page > lastPageIndex) {
        setPage(lastPageIndex);
        await refreshPageCacheSafely(lastPageIndex);
      }

      await refreshVisitedPageCachesSafely();
    },
    [
      apiClient,
      detailId,
      page,
      pageSize,
      refreshPageCacheSafely,
      refreshVisitedPageCachesSafely,
      swrResponse.data?.count,
    ]
  );

  const changePage = useCallback((newPage: number): void => {
    setPage(newPage);
  }, []);

  const changePageSize = useCallback((newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(0);
  }, []);

  return {
    files: swrResponse.data?.list ?? [],
    totalCount: swrResponse.data?.count ?? 0,
    page,
    pageSize,
    changePage,
    changePageSize,
    isLoading:
      detailId !== null &&
      shouldFetch &&
      !swrResponse.error &&
      !swrResponse.data,
    isError: swrResponse.error != null,
    refetch,
    uploadFiles,
    retryFailedUploads,
    deleteFile,
    updateDisplayName,
    getDownloadInfo,
    isUploading,
    uploadProgress,
    failedUploads,
    lastUploadSummary,
    resetUploadState,
  };
};

/**
 * 資料管理画面向けに、資料一覧取得・アップロード・再試行・削除・ダウンロード情報取得を管理するフック。
 *
 * @param detailId 資料対象のプロット詳細ID
 * @param shouldFetch 一覧APIを実行するかどうか
 * @returns 画面表示用データと各操作ハンドラ
 */
export const useMapPlotRequestDetailFiles = (
  detailId: number | null,
  shouldFetch: boolean,
  query: MapPlotRequestDetailFilesQuery = DEFAULT_QUERY
): MapPlotRequestDetailFilesResult => {
  const { apiClient } = useApiClient();

  return useMapPlotRequestDetailFilesWithApiClient(
    apiClient,
    detailId,
    shouldFetch,
    query
  );
};
